import { FileUp, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ComponentProps } from 'react';

import { cn } from '@shared/lib/utils';

import { Spinner } from '../feedback/spinner';
import { Button } from '../primitives/button';

type FilePickerBaseProps = Omit<
  ComponentProps<'input'>,
  'id' | 'multiple' | 'onChange' | 'type' | 'value'
> & {
  changeLabel: string;
  chooseLabel: string;
  error?: string;
  existingFile?: {
    name: string;
    previewUrl?: string;
  };
  helperText: string;
  id: string;
  isPreviewLoading?: boolean;
  previewAlt: string;
  previewLoadingLabel?: string;
};

export type FilePickerProps = FilePickerBaseProps &
  (
    | {
        multiple?: false;
        file: File | null;
        onFileChange: (file: File | null) => void;
        removeLabel: string;
        files?: never;
        onFilesChange?: never;
      }
    | {
        multiple: true;
        files: File[];
        onFilesChange: (files: File[]) => void;
        removeLabel: (file: File) => string;
        file?: never;
        onFileChange?: never;
        existingFile?: never;
      }
  );

function isPreviewableImage(file: File | null): file is File {
  return file !== null && ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
}

export function FilePicker(props: FilePickerProps) {
  const {
    changeLabel,
    chooseLabel,
    className,
    disabled = false,
    error,
    existingFile,
    file,
    files,
    helperText,
    id,
    isPreviewLoading = false,
    multiple,
    onFileChange,
    onFilesChange,
    previewAlt,
    previewLoadingLabel,
    removeLabel,
    ...inputProps
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreviewUrls, setLocalPreviewUrls] = useState(new Map<File, string>());
  const [isDragging, setIsDragging] = useState(false);
  const helperId = `${id}-helper`;
  const selectionId = `${id}-selection`;
  const errorId = `${id}-error`;

  useEffect(() => {
    const previews = new Map<File, string>();
    for (const selected of files ?? (file ? [file] : []))
      if (isPreviewableImage(selected)) previews.set(selected, URL.createObjectURL(selected));
    setLocalPreviewUrls(previews);
    return () => {
      for (const url of previews.values()) URL.revokeObjectURL(url);
    };
  }, [file, files]);

  useEffect(() => {
    if ((multiple || file === null) && inputRef.current !== null) inputRef.current.value = '';
  }, [file, files, multiple]);

  const selectedFiles: { name: string; previewUrl: string | undefined; file: File | null }[] = (
    files ?? (file ? [file] : [])
  ).map((selected) => ({
    name: selected.name,
    previewUrl: localPreviewUrls.get(selected),
    file: selected,
  }));
  if (!multiple && file === null && existingFile)
    selectedFiles.push({ ...existingFile, previewUrl: existingFile.previewUrl, file: null });
  const hasSelection = selectedFiles.length > 0;
  const chooseFiles = (incoming: File[]) => {
    if (disabled) return;
    if (multiple && incoming.length === 0) return;
    if (multiple) onFilesChange([...new Set([...files, ...incoming])]);
    else onFileChange(incoming[0] ?? null);
  };
  const describedBy = [hasSelection ? selectionId : helperId, error ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div
      role="presentation"
      onDragOver={
        multiple
          ? (event) => {
              event.preventDefault();
              if (!disabled) setIsDragging(true);
            }
          : undefined
      }
      onDragLeave={
        multiple
          ? () => {
              setIsDragging(false);
            }
          : undefined
      }
      onDrop={
        multiple
          ? (event) => {
              event.preventDefault();
              setIsDragging(false);
              chooseFiles(Array.from(event.dataTransfer.files));
            }
          : undefined
      }
      className={cn(
        'relative rounded-none border border-input bg-background transition-colors hover:border-foreground/40 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        hasSelection && !error && 'border-primary',
        isDragging && !error && 'border-primary bg-primary/5',
        error && 'border-destructive',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <input
        {...inputProps}
        ref={inputRef}
        id={id}
        type="file"
        multiple={multiple}
        className="sr-only"
        disabled={disabled}
        aria-describedby={describedBy || undefined}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          chooseFiles(Array.from(event.target.files ?? []));
          if (multiple) event.target.value = '';
        }}
      />

      <label
        htmlFor={id}
        className={cn('absolute inset-0 z-0 cursor-pointer', disabled && 'cursor-not-allowed')}
      >
        <span className="sr-only">{hasSelection ? changeLabel : chooseLabel}</span>
      </label>

      <div className="pointer-events-none relative z-10 min-h-20 p-4">
        {!hasSelection ? (
          <div className="flex min-h-11 flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
            <FileUp aria-hidden="true" className="size-5 shrink-0" />
            <span className="text-sm font-medium text-foreground">{chooseLabel}</span>
            <span id={helperId} className="text-sm text-muted-foreground">
              {helperText}
            </span>
          </div>
        ) : (
          <div id={selectionId} className="flex w-full flex-col gap-3">
            {selectedFiles.map((selectedFile, index) => (
              <div key={`${selectedFile.name}-${String(index)}`} className="space-y-3">
                <div className="flex min-w-0 items-center justify-between gap-3 text-sm text-foreground">
                  <span className="min-w-0 break-all text-left">{selectedFile.name}</span>
                  {selectedFile.file !== null ? (
                    <Button
                      type="button"
                      variant="destructiveGhost"
                      size="icon"
                      disabled={disabled}
                      aria-label={multiple ? removeLabel(selectedFile.file) : removeLabel}
                      title={multiple ? removeLabel(selectedFile.file) : removeLabel}
                      className="pointer-events-auto shrink-0"
                      onClick={() => {
                        if (inputRef.current !== null) inputRef.current.value = '';
                        if (multiple)
                          onFilesChange(files.filter((_, fileIndex) => fileIndex !== index));
                        else onFileChange(null);
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
                {isPreviewLoading ? (
                  <div
                    aria-busy="true"
                    className="flex h-64 w-full items-center justify-center border border-border bg-muted/30"
                  >
                    <Spinner size="md" aria-label={previewLoadingLabel ?? 'Loading'} />
                  </div>
                ) : selectedFile.previewUrl !== undefined ? (
                  <div className="w-full overflow-hidden border border-border bg-muted/30">
                    <img
                      src={selectedFile.previewUrl}
                      alt={
                        selectedFile.file === null
                          ? selectedFile.name
                          : multiple
                            ? `${previewAlt}: ${selectedFile.name}`
                            : previewAlt
                      }
                      className="max-h-64 w-full object-contain"
                    />
                  </div>
                ) : null}
              </div>
            ))}
            {multiple ? (
              <div className="flex min-h-11 flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
                <FileUp aria-hidden="true" className="size-5 shrink-0" />
                <span className="text-sm font-medium text-foreground">{changeLabel}</span>
                <span id={helperId} className="text-sm text-muted-foreground">
                  {helperText}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="relative z-10 px-4 pb-4 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
